<template>
  <div class="login-wrapper">
    <div class="login-card">
      <img src="@/assets/images/big-logo.png" alt="Makalin Clinic" class="logo">
      <h1 class="welcome-title">ยินดีต้อนรับ</h1>
      <p class="welcome-desc">เข้าสู่ระบบเพื่อซื้อสินค้าและใช้คูปองของคุณ</p>

      <button type="button" class="btn-line" @click="loginWithLine">
        <img src="@/assets/icons/white-line.svg" alt="LINE" class="btn-icon">
        <span>Login with LINE กดซ้ำ 2-3 ครั้ง</span>
      </button>

      <button type="button" class="btn-phone" @click="goToLoginWithPhone">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A2E8F" stroke-width="1.8" stroke-linecap="round">
          <rect x="6.8" y="2.6" width="10.4" height="18.8" rx="2.6"/>
          <path d="M11 18.4h2"/>
        </svg>
        <span>เข้าสู่ระบบด้วยเบอร์มือถือ</span>
      </button>

      <p class="terms-text">การเข้าสู่ระบบถือว่ายอมรับเงื่อนไขการใช้บริการของ Makalin Clinic</p>
    </div>
  </div>
</template>

<script>
import { computed, watchEffect, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import liff from '@line/liff'

// Shared across the component's lifetime on this page load.
// Prevents calling liff.init() more than once (double-init causes errors on Android).
let liffInitPromise = null

// Key used to signal that a LINE login redirect was initiated so the page
// auto-retries on the way back — cleared before the dispatch so it never loops.
const LINE_PENDING_KEY = 'line_login_pending'

export default {
  name: 'UserLogin',
  setup() {
    /** context */
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const { t } = useI18n()

    /** computed */
    const getUser = computed(() => store.getters['user/getUser'])
    const shouldAttachCurrentUser = computed(() => route.query.attach_line === '1')
    const SYNC_LINE_TOKEN_KEY = 'line_sync_access_token'

    const storeSyncRewardsLineToken = () => {
      const accessToken = liff.getAccessToken()
      if (accessToken) {
        sessionStorage.setItem(SYNC_LINE_TOKEN_KEY, accessToken)
      } else {
        try { console.error('[LINE auth] getAccessToken() empty (sync flow) | liffId=' + (process.env.VUE_APP_LINE_LIFE_ID || '(missing)')) } catch (e) { /* noop */ }
      }
      return accessToken
    }

    /** method */
    const goToRegister = () => router.push('/user/register')
    const goToLoginWithPhone = () => router.push('/user/login-with-phone')
    const lineDebug = (msg) => {
      try {
        console.error('[LINE auth] ' + msg + ' | liffId=' + (process.env.VUE_APP_LINE_LIFE_ID || '(missing)'))
      } catch (e) { /* noop */ }
    }
    const showLineError = (err) => {
      store.commit('loading/SET_DATA', { id: 'login-line', data: false })
      store.commit('snackBar/SET_DATA', {
        message: err?.detail || err?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบด้วย LINE กรุณาลองใหม่อีกครั้ง',
        color: 'error',
      })
    }

    // Initialises LIFF exactly once per page load. Subsequent calls return the same Promise.
    // On failure the promise is cleared so the next attempt retries from scratch.
    const doLiffInit = () => {
      if (!liffInitPromise) {
        liffInitPromise = liff.init({ liffId: process.env.VUE_APP_LINE_LIFE_ID }).catch(err => {
          liffInitPromise = null
          throw err
        })
      }
      return liffInitPromise
    }

    const dispatchLineLogin = async (extra = {}) => {
      const MAX_RETRIES = 3
      let lastErr
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        const token = liff.getAccessToken()
        if (!token) {
          lineDebug(`getAccessToken() empty (attempt ${attempt}/${MAX_RETRIES})`)
          lastErr = { message: 'ไม่สามารถรับ token จาก LINE ได้ กรุณาลองใหม่' }
          if (attempt < MAX_RETRIES) await new Promise(r => setTimeout(r, 300 * attempt))
          continue
        }
        try {
          await store.dispatch('user/fetchLoginWithLine', {
            lineAccessToken: token,
            attachCurrentUser: shouldAttachCurrentUser.value,
            ...extra,
          })
          return  // success — watchEffect handles navigation
        } catch (err) {
          lineDebug(`fetchLoginWithLine failed (attempt ${attempt}/${MAX_RETRIES}): ${err?.message || JSON.stringify(err)}`)
          lastErr = err
          if (attempt < MAX_RETRIES) await new Promise(r => setTimeout(r, 300 * attempt))
        }
      }
      showLineError(lastErr)
    }

    const loginWithLine = () => {
      const isInClient = liff.isInClient()
      const path = route.query?.path
      store.commit('loading/SET_DATA', { id: 'login-line', data: true })
      doLiffInit().then(() => {
        if (isInClient) {
          if (path === 'sync-rewards' && !shouldAttachCurrentUser.value) {
            store.commit('loading/SET_DATA', { id: 'login-line', data: false })
            storeSyncRewardsLineToken()
            router.replace('/sync-rewards')
            return
          }
          dispatchLineLogin()
        }
        else {
          if (!liff.isLoggedIn()) {
            const destinationUrl = new URL(`${window.location.origin}/user/login`)
            if (path) destinationUrl.searchParams.set('path', path)
            if (shouldAttachCurrentUser.value) destinationUrl.searchParams.set('attach_line', '1')
            // Store pending flag so onMounted auto-retries after the OAuth redirect
            // returns, even when liff.isLoggedIn() is briefly false on the way back.
            try { localStorage.setItem(LINE_PENDING_KEY, '1') } catch (e) { /* localStorage unavailable */ }
            // Android app-to-app: if LINE app returns without a full-page redirect,
            // visibilitychange fires when the tab regains focus — handle it here.
            const onVisible = () => {
              if (document.visibilityState === 'visible') {
                document.removeEventListener('visibilitychange', onVisible)
                try { localStorage.removeItem(LINE_PENDING_KEY) } catch (e) { /* localStorage unavailable */ }
                if (liff.isLoggedIn()) {
                  dispatchLineLogin()
                } else {
                  store.commit('loading/SET_DATA', { id: 'login-line', data: false })
                }
              }
            }
            document.addEventListener('visibilitychange', onVisible)
            liff.login({ redirectUri: destinationUrl.toString() })
          }
          else {
            if (path === 'sync-rewards' && !shouldAttachCurrentUser.value) {
              store.commit('loading/SET_DATA', { id: 'login-line', data: false })
              storeSyncRewardsLineToken()
              router.replace('/sync-rewards')
              return
            }
            dispatchLineLogin()
          }
        }
      }).catch((err) => {
        lineDebug('liff.init() failed: ' + (err?.message || JSON.stringify(err)))
        showLineError(err)
      })
    }
    const back = () => {
      router.back()
    }

    /** mounted */
    onMounted(() => {
      const isInClient = liff.isInClient()
      const s = route.query.s
      const path = route.query?.path
      const loggedIn = route.query.loggedIn

      // If the user pressed "Login with LINE" and was redirected to LINE OAuth,
      // a pending flag was stored in localStorage. On return (full page reload)
      // we clear it immediately (no loop risk) and auto-dispatch login — this
      // fires even when liff.isLoggedIn() is still briefly false after init.
      if (!isInClient) {
        let hasPending = false
        try { hasPending = localStorage.getItem(LINE_PENDING_KEY) === '1' } catch (e) { /* localStorage unavailable */ }
        if (hasPending) {
          try { localStorage.removeItem(LINE_PENDING_KEY) } catch (e) { /* localStorage unavailable */ }
          store.commit('loading/SET_DATA', { id: 'login-line', data: true })
          doLiffInit().then(() => {
            if (path === 'sync-rewards' && !shouldAttachCurrentUser.value) {
              storeSyncRewardsLineToken()
              router.replace('/sync-rewards')
              return
            }
            dispatchLineLogin(s ? { s } : {})
          }).catch((err) => {
            lineDebug('liff.init() failed (pending retry): ' + (err?.message || JSON.stringify(err)))
            showLineError(err)
          })
          return
        }
      }

      if (path && !loggedIn) {
        // The sync-rewards flow manages its own post-login navigation (it decides
        // /my-coupons vs the phone modal). Setting 'redirectTo' here would later make
        // fetchGetUserProfile hard-redirect (window.location.href) back to /sync-rewards,
        // overriding that and bouncing the user off the coupon page.
        if (path !== 'sync-rewards') {
          sessionStorage.setItem('redirectTo', `/${path}`)
        }
        loginWithLine()
        return
      }

      if (isInClient) {
        store.commit('loading/SET_DATA', { id: 'login-line', data: true })
        doLiffInit().then(() => {
          if (path === 'sync-rewards' && !shouldAttachCurrentUser.value) {
            store.commit('loading/SET_DATA', { id: 'login-line', data: false })
            storeSyncRewardsLineToken()
            router.replace('/sync-rewards')
            return
          }
          dispatchLineLogin(s ? { s } : {})
        }).catch((err) => {
          lineDebug('liff.init() failed (mounted in-client): ' + (err?.message || JSON.stringify(err)))
          showLineError(err)
        })
        return
      }

      // External browser: always init LIFF and auto-login if LINE says we are logged in.
      // This covers returning from LINE OAuth redirect AND an existing cached LINE session.
      // No ?loggedIn param check needed — liff.isLoggedIn() is the source of truth.
      if (!isInClient) {
        doLiffInit().then(() => {
          if (!liff.isLoggedIn()) return  // not logged in — show login page normally

          // isLoggedIn=true but getAccessToken() may still be null for a brief moment
          // after init (LIFF race condition). dispatchLineLogin already retries on null
          // token, so we hand off to it directly instead of bailing out here.
          if (path === 'sync-rewards' && !shouldAttachCurrentUser.value) {
            storeSyncRewardsLineToken()
            router.replace('/sync-rewards')
            return
          }

          store.commit('loading/SET_DATA', { id: 'login-line', data: true })
          dispatchLineLogin(s ? { s } : {})
        }).catch((err) => {
          lineDebug('liff.init() failed (mounted external): ' + (err?.message || JSON.stringify(err)))
          // Silently show login page — user can click the button to retry
        })
      }
    })

    onUnmounted(() => {
      store.commit('loading/SET_DATA', { id: 'login-line', data: false })
      try { localStorage.removeItem(LINE_PENDING_KEY) } catch (e) { /* localStorage unavailable */ }
    })

    /** watch */
    watchEffect(() => {
      // Only act while THIS login page is the active route. After we router.replace()
      // elsewhere (e.g. /sync-rewards), this effect can fire once more during the
      // transition with a changed route (path becomes undefined) before it unmounts —
      // without this guard it falls through to fetchGetUserProfile('/my-profile') and
      // hijacks the sync-rewards flow, sending the user to profile instead of register.
      if (route.name !== 'UserLogin') return
      if (getUser.value) {
        const s = route.query.s
        const path = route.query.path
        const attachLine = route.query.attach_line === '1'
        const loggedIn = route.query.loggedIn
        if (path === 'sync-rewards' && !attachLine) {
          return
        }
        if (attachLine && path === 'sync-rewards' && !loggedIn) {
          return
        }
        if (path === 'sync-rewards') {
          router.replace('/sync-rewards')
          return
        }
        // After a LINE OAuth (liff.login) round-trip, the original path is buried inside
        // `liff.state` (route.query.path is undefined), so the checks above miss it and we
        // would wrongly fall through to /my-profile. Detect the sync-rewards flow from
        // liff.state / the URL, capture the LINE token, and hand back to sync-rewards.
        const rawLiffState = route.query['liff.state']
        const isSyncViaState = (typeof rawLiffState === 'string' && rawLiffState.includes('sync-rewards'))
          || (typeof window !== 'undefined' && String(window.location.href).includes('sync-rewards'))
        if (isSyncViaState) {
          try { storeSyncRewardsLineToken() } catch (e) { /* noop */ }
          router.replace('/sync-rewards')
          return
        }
        if (s) {
          store.dispatch('user/fetchGetUserProfile', '/my-cart')
        }
        else {
          store.dispatch('user/fetchGetUserProfile', '/my-profile')
        }
      }
    })
    return {
      t,
      goToLoginWithPhone,
      loginWithLine,
      getUser,
      shouldAttachCurrentUser,
      back,
      goToRegister,
    }
  }
}
</script>

<style lang="scss" scoped>
.login-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px 56px;
  background: linear-gradient(135deg, #c9cbf8 0%, #bfc2f6 45%, #cdcffa 100%);
}

.login-card {
  width: 100%;
  max-width: 408px;
  background: #fff;
  border-radius: 24px;
  padding: 40px 36px 30px;
  box-shadow: 0 24px 50px rgba(32, 36, 110, 0.20);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo {
  width: 150px;
  height: auto;
  display: block;
}

.welcome-title {
  margin-top: 22px;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #1B1F52;
  text-align: center;
}

.welcome-desc {
  margin-top: 7px;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 400;
  font-size: 15.5px;
  line-height: 1.65;
  color: #5A5F86;
  text-align: center;
  max-width: 290px;
}

.btn-line {
  margin-top: 26px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  padding: 17px;
  border: none;
  border-radius: 14px;
  background: #06C755;
  color: #fff;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 700;
  font-size: 17.5px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(6, 199, 85, 0.32);
  transition: background 0.16s, transform 0.16s, box-shadow 0.16s;

  &:hover {
    background: #05b84e;
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(6, 199, 85, 0.38);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 6px 14px rgba(6, 199, 85, 0.28);
  }
}

.btn-phone {
  margin-top: 12px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  border: 1.5px solid #d9dbee;
  border-radius: 14px;
  background: #fff;
  color: #2A2E8F;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 600;
  font-size: 17px;
  cursor: pointer;
  transition: border-color 0.16s, background 0.16s;

  &:hover {
    border-color: #2A2E8F;
    background: #f5f5fc;
  }

  &:active {
    background: #eeeef8;
  }
}

.btn-icon {
  width: 26px;
  height: 26px;
  display: block;
  border-radius: 6px;
}

.terms-text {
  margin-top: 22px;
  font-family: 'IBM Plex Sans Thai', system-ui, sans-serif;
  font-weight: 400;
  font-size: 12.5px;
  line-height: 1.6;
  color: #5A5F86;
  text-align: center;
  max-width: 300px;
}

@media screen and (max-width: 460px) {
  .login-card {
    padding: 32px 24px 28px;
  }

  .welcome-title {
    font-size: 22px;
  }

  .btn-line,
  .btn-phone {
    font-size: 16px;
    padding: 15px;
  }
}
</style>
